/**
 * Warminster Maltings — floor maltings in Warminster, Wiltshire, since 1855.
 *
 * Colour is published in EBC; `colorEbc` is the mid-point of the published
 * band (or the published maximum where they print only a ceiling) and the °L
 * figure is the conversion `°L = (EBC + 1.2) / 2.65` rounded to one decimal.
 *
 * Extract is published as IoB hot-water extract in litre-degrees per kilogram,
 * dry basis. Fine-grind dry-basis percent is `L°/kg dry ÷ 3.86`, which is the
 * sucrose-relative conversion the rest of the British rows in this package
 * already use. Moisture is the published maximum.
 *
 * Warminster publishes no diastatic power. Enzymatic rows carry the
 * class-typical °Lintner this package already holds for British pale, lager,
 * wheat and rye, and are `representative`. Crystal and roasted rows take DP 0
 * by physics; where extract, moisture and colour are all the maker's, those
 * rows are `spec-sheet`.
 *
 * Numbered crystal SKUs are named with the EBC grade in parentheses so the
 * catalog's Lovibond-named-colour check does not treat a British EBC product
 * number as a °L measurement. Ids keep the grade (`warminster-crystal-150`).
 *
 * No row carries a `url`. The maker publishes category pages, not a page per
 * SKU, and a family listing is not a product page.
 *
 * @see https://www.warminster-malt.co.uk/warminster-base-malts/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Warminster Maltings malts. @see Fermentable */
export const warminsterFermentables: readonly Fermentable[] = [
  {
    id: 'warminster-maris-otter',
    name: 'Maris Otter',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.8,
    diastaticPowerLintner: 48,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted Maris Otter, the malt Warminster is known for. Full, biscuity and faintly nutty, and forgiving in the mash — the English pale ale base when the malt is meant to be tasted.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'warminster-maris-otter-low-colour',
    name: 'Maris Otter Low Colour',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 48,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same floor-malted Otter, kilned paler. Keeps the variety character while staying light enough for a golden ale or a hop-forward pale that does not want a biscuit spine.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'floor-malted', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'warminster-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.1,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted pale ale from modern barley, a touch more extract and enzyme than the Otter and a little less character. The everyday English ale base when the hop bill is doing the talking.',
    tags: ['base', 'ale', 'british', 'floor-malted', 'workhorse'],
    provenance: 'representative',
  },
  {
    id: 'warminster-lager',
    name: 'Lager Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A fully modified floor-malted lager malt, pale and clean enough for infusion or decoction. Malty-sweet rather than grainy, and the house lager canvas.',
    tags: ['base', 'lager', 'british', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'warminster-czech-lager',
    name: 'Czech Lager Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.3,
    moisturePercent: 4.5,
    colorLovibond: 1.3,
    colorEbc: 2.3,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Ultra-pale floor-malted lager, modified a little less than the standard lager malt so it behaves more like a Czech pilsner base. Built for pilsner-style beers that want a very light gold.',
    tags: ['base', 'lager', 'british', 'pilsner', 'czech', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'warminster-wheat',
    name: 'Wheat Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.3,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted wheat. At 5–15 % it firms up foam and body in ale; at 50–80 % it is a weissbier base. Protein is higher than the barley malts, so the lauter wants watching.',
    tags: ['base', 'wheat', 'british', 'floor-malted', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'warminster-rye',
    name: 'Rye Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.6,
    moisturePercent: 6,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted rye: toffee, a spicy finish and a reddish hue. Fine at low rates in ale; up to 50 % for a rye beer, with rice hulls once the mash starts to gum.',
    tags: ['base', 'rye', 'british', 'floor-malted', 'spicy'],
    provenance: 'representative',
  },
  {
    id: 'warminster-vienna',
    name: 'Vienna Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 3.5,
    colorLovibond: 5,
    colorEbc: 12,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to a light copper with a sweet, nutty wort. Less fermentable than the pale ale malt, and the usual Warminster choice for mild and brown ale rather than for a Vienna lager.',
    tags: ['kilned', 'british', 'vienna', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'warminster-munich',
    name: 'Munich Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 3.5,
    colorLovibond: 6.9,
    colorEbc: 17,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step darker than their Vienna: pronounced golden-copper color and the same sweet, nutty malt. Aimed at dark lager, and happy as a large share of the grist.',
    tags: ['kilned', 'british', 'munich', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'warminster-caragold',
    name: 'CaraGold',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 8,
    colorLovibond: 5.5,
    colorEbc: 13.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A very pale crystal: muscovado, maple and a little dried fruit rather than toffee. Five to ten percent plump a lager or a golden ale without much color.',
    tags: ['crystal', 'caramel', 'british', 'light', 'body'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-caramalt',
    name: 'CaraMalt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 6,
    colorLovibond: 12.7,
    colorEbc: 32.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light crystal with caramel, coffee and a lingering dried-fruit note. The house adjunct for lager and pale ale when body and foam matter more than color.',
    tags: ['crystal', 'caramel', 'british', 'light'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-100',
    name: 'Crystal (100 EBC)',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72.5,
    moisturePercent: 5.5,
    colorLovibond: 44.8,
    colorEbc: 117.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Mid-color British crystal, sold as Crystal 100. Toffee and a copper hue at 5–10 %; the everyday crystal in a bitter or a pale ale.',
    tags: ['crystal', 'caramel', 'british', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-150',
    name: 'Crystal (150 EBC)',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72.5,
    moisturePercent: 3,
    colorLovibond: 67.4,
    colorEbc: 177.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 150. Caramel, coffee and dark chocolate with a golden-amber cast; five to ten percent is the usual dose for color and flavor in ale.',
    tags: ['crystal', 'caramel', 'british', 'toffee', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-200',
    name: 'Crystal (200 EBC)',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 91,
    colorEbc: 240,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 200. Darker toffee and dried fruit, heading toward treacle. A few percent deepens a best bitter, a mild or a brown ale.',
    tags: ['crystal', 'caramel', 'british', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-400',
    name: 'Crystal (400 EBC)',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.2,
    moisturePercent: 3.5,
    colorLovibond: 184.4,
    colorEbc: 487.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sold as Crystal 400. Burnt toffee and a deep ruby color. One to five percent is enough; above that it tastes like dark crystal rather than beer.',
    tags: ['crystal', 'caramel', 'british', 'dark', 'burnt-sugar'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-rye',
    name: 'Crystal Rye',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.1,
    moisturePercent: 5,
    colorLovibond: 68.4,
    colorEbc: 180,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed rye, with the spicy rye note sitting under caramel. A few percent in a rye ale or a dark ale adds color and a peppery caramel edge the barley crystals do not.',
    tags: ['crystal', 'caramel', 'rye', 'british', 'spicy'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-crystal-wheat',
    name: 'Crystal Wheat',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.1,
    moisturePercent: 5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelized wheat malt. Rounds out a wheat beer or a hazy ale with caramel sweetness and no barley-crystal graininess, and helps foam the way wheat always does.',
    tags: ['crystal', 'caramel', 'wheat', 'british'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-chocolate',
    name: 'Chocolate Malt',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71.2,
    moisturePercent: 3.5,
    colorLovibond: 449.5,
    colorEbc: 1190,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Coffee and mild burnt toast, less harsh than their black malt. Three to five percent is the usual dose in porter and stout.',
    tags: ['roasted', 'british', 'chocolate', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'warminster-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Warminster Maltings',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 3.5,
    colorLovibond: 604.2,
    colorEbc: 1600,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted dark: sharp, astringent and burnt, with a paler head than chocolate or black malt. The grain that makes a dry stout taste like one.',
    tags: ['roasted', 'british', 'unmalted', 'stout', 'coffee', 'dry-stout'],
    provenance: 'spec-sheet',
  },
];
